import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
import type { Quarter, QuarterlyReviewStatus } from "@/types/entities";
import { toast } from "sonner";

export interface QuarterlyReviewFilters {
  employee_id?: string;
  supervisor_id?: string;
  year?: number;
  quarter?: Quarter;
  status?: QuarterlyReviewStatus;
  cursor?: string;
  limit?: number;
}

export function useQuarterlyReviews(filters: QuarterlyReviewFilters = {}) {
  return useQuery({
    queryKey: ["quarterly-reviews", filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/reviews", {
        params: { query: filters },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useQuarterlyReview(id: string) {
  return useQuery({
    queryKey: ["quarterly-reviews", id],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/reviews/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateQuarterlyReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateQuarterlyReviewRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/reviews", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["quarterly-reviews"] });
      toast.success("Quarterly review created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useSubmitSelfReflection(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (selfReflection: string) => {
      const { data, error } = await apiClient.PATCH("/v1/reviews/{id}", {
        params: { path: { id } },
        body: { self_reflection: selfReflection },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["quarterly-reviews"] });
      toast.success("Self-reflection submitted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useSubmitSupervisorFeedback(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (supervisorFeedback: string) => {
      const { data, error } = await apiClient.PATCH("/v1/reviews/{id}", {
        params: { path: { id } },
        body: { supervisor_feedback: supervisorFeedback },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["quarterly-reviews"] });
      toast.success("Review finalized");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
